import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
} from '@salescore/core'
import { App } from 'antd'
import { Array, Option, pipe } from 'effect'
import { t } from 'i18next'

import { isCheckboxColumn, type RSheetColumn, type RSheetRecordNode } from '../../../../domain/column.ts'
import { useUpsertViewRecordsEntry } from '../../../../hooks/useUpsertViewRecordsEntry'
import { isKpiOrKpiPivotContext, type RSheetContext } from '../../../../recoil/context'
import { useCursorMutation, useGetCursorSnapshot } from '../../../../recoil/cursor'
import { useRecordsImpl } from '../../../../recoil/records'
import type { SalescoreObjectAbility, ViewAbility } from '../../../../recoil/view/atoms'
import { useChangeSystemField } from '../../../hooks/useChangeSystemField'
import { isCellWriteProhibited } from '../../../util/cellWriteProhibition'
import { getActiveConditionalEffects } from '../../../util/conditionalEffects'
import { getCellRelated } from '../../../util/getCellValue'
import { getEditTargetModelName } from '../../../util/getEditTargetModelName'

export const useClickCurrentCursorMutation = (
  context: RSheetContext,
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig,
  ability: ViewAbility & SalescoreObjectAbility,
  query: ViewQueryList | undefined,
  visibleRowNumber: number | undefined,
  // eslint-disable-next-line @typescript-eslint/max-params
) => {
  const getCursorSnapshot = useGetCursorSnapshot()
  const rows = useRecordsImpl()
  const { message } = App.useApp()
  const upsertViewRecordsEntry = useUpsertViewRecordsEntry(query, ability)
  const setChangeSystemField = useChangeSystemField(
    context.fixedLeftColumnIndex,
    columns,
    compiledConfig,
    // query が undefined なのは kpi/kpiPivot view 経由で mount された場合のみ。
    // kpi/kpiPivot の column 型では useChangeSystemField 内の 'open' 分岐 (streamName を
    // 参照する箇所) に到達しないため、undefined を渡しても実害なし。
    query?.tree.meta.dependedStreamNames[0],
    visibleRowNumber,
  )
  const { onClickCurrentCursor } = useCursorMutation(ability)

  // eslint-disable-next-line complexity,sonarjs/cognitive-complexity -- Redux 移行のため複雑性を一時的に許容する
  return ({ pressedKey }: { pressedKey?: string }): void => {
    const cursor = getCursorSnapshot()
    if (cursor === undefined) {
      return
    }
    const column = columns[cursor.main.columnIndex]
    const row = rows[cursor.main.rowIndex]
    if (column === undefined || row === undefined) {
      return
    }

    const { recordNode, value, isBlankFillerCell } = getCellRelated(column, row, cursor.main.innerRowIndex)
    if (isBlankFillerCell) {
      return
    }

    // checkbox系であれば、編集モードにするのではなく、直接値をtoggleする
    const toggledValue = typeof value === 'boolean' ? !value : true
    if (recordNode !== undefined && isCheckboxColumn(column)) {
      if (cursor.expand !== undefined) {
        const up = Math.min(cursor.main.rowIndex, cursor.expand.rowIndex)
        const down = Math.max(cursor.main.rowIndex, cursor.expand.rowIndex)
        pipe(
          Array.range(up, down),
          Array.map((rowIndex) => Array.get(rows, rowIndex).pipe(Option.getOrUndefined)),
          Array.forEach((row) => {
            setChangeSystemField({
              value: toggledValue,
              row,
              innerRowIndex: 0,
              column,
            })
          }),
        )
        return
      } else if (column.isSystem === true) {
        setChangeSystemField({
          value: toggledValue,
          row,
          innerRowIndex: cursor.main.innerRowIndex,
          column,
        })
        return
      }
    }

    // レコードURLであれば、リンクを開く
    if (recordNode !== undefined && column.metaType === 'record_url' && typeof value === 'string') {
      window.open(value)
      return
    }

    // open系であればonChangeする
    if ((recordNode !== undefined && column.type === 'open') || column.type === 'kpi') {
      setChangeSystemField({
        value,
        row,
        innerRowIndex: cursor.main.innerRowIndex,
        column,
      })
      return
    }

    // 書き込み禁止
    if (column.readonly === true) {
      // eslint-disable-next-line sonarjs/todo-tag
      // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
      void message.warning(
        isKpiOrKpiPivotContext(context) ? t(`集計表示中は編集できません`) : t(`このカラムは書き込み禁止です`),
      )
      return
    }
    // 空セルかつchildrenの関係のとき、checkbox/boolean は編集モードを持たない (BooleanInputCell は空) ため、
    // 作成 + 値トグルを useAsyncHandleKeyDown 側で行う。ここで編集モードに入れると editing.isEditing===true により
    // useAsyncHandleKeyDown が早期 return し作成処理が動かないため、return する。
    // 作成と同時に入れる値は新規作成の一部なので、後ろの編集権限の判定より前に抜ける (作成できるかは addChildRecord が判定する)
    if (
      recordNode === undefined &&
      isCheckboxColumn(column) &&
      (column.nodeType === 'children' || column.nodeType === 'child')
    ) {
      return
    }
    if (recordNode !== undefined) {
      const effects = getActiveConditionalEffects(column.conditionalEffects, recordNode, {})
      const disabled = effects.some((x) => x.effect === 'disable')
      if (disabled) {
        // eslint-disable-next-line sonarjs/todo-tag
        // TODO: feedbackMessagesAtomの名残でエラー時のフィードバックの責務が混入していて良くない
        void message.warning(t(`このセルは書き込み禁止です`))
        return
      }
    } else if (!isCheckboxColumn(column) && isCellWriteProhibited(column, row, query)) {
      // 空セル (非 checkbox) が条件付き disable のとき、編集モードに入らせない。判定は新規レコードの内容で行う (作成後の状態とそろえる)。
      // checkbox の空セルは useAsyncHandleKeyDown / SheetValueEmptyCell 側で通知・抑止するため、ここでは対象外 (二重通知回避)。
      void message.warning(t(`このセルは書き込み禁止です`))
      return
    }
    const editTargetModelName = getEditTargetModelName({ column, recordNode, query })
    if (!ability.canEditModelRecord(editTargetModelName)) {
      void message.warning(t(`このシートのレコードを編集する権限がありません`))
      return
    }
    if (recordNode !== undefined && column.field !== undefined && isCheckboxColumn(column)) {
      // チェックボックスのチェックを更新
      upsertViewRecordsEntry({
        recordChanges: [
          {
            rowIndex: cursor.main.rowIndex,
            innerRowIndex: cursor.main.innerRowIndex,
            isNestedColumn: column.node.path.length > 1,
            nodePath: column.node.path,
            field: column.field,
            value: toggledValue,
            label: undefined,
          },
        ],
      })
      return
    }
    // 空セルかつchildrenの関係 (checkbox 以外) であれば、レコード作成は useAsyncHandleKeyDown 側で行い、
    // ここでは return せず編集モードに入る (作成後に即編集できるようにするため)

    onClickCurrentCursor(column, pressedKey)
  }
}
